import type { FaqCategoryWithItems } from "models/knowledge-base/faq";
import { storeToRefs } from "pinia";
import { useFaqStore } from "store/modules/faqStore";
import { computed, type ComputedRef } from "vue";

export interface FaqTreeNode extends FaqCategoryWithItems {
  children: FaqCategoryWithItems[];
  selectable: boolean;
}

/**
 * Builds a 2-level tree (parent → children) from the flat FAQ category list.
 * A parent is `selectable` when it has its own items; otherwise it acts as a header
 * for its children. Only parents and children that have items are returned.
 */
export const useFaqTree = (): {
  tree: ComputedRef<FaqTreeNode[]>;
} => {
  const faqStore = useFaqStore();
  const { categories } = storeToRefs(faqStore);

  const tree = computed<FaqTreeNode[]>(() => {
    const all = categories.value;
    const parents = all.filter((c) => c.parent_uuid === null);

    return parents
      .map<FaqTreeNode>((parent) => {
        const children = all
          .filter((c) => c.parent_uuid === parent.uuid && c.items.length > 0)
          .slice()
          .sort((a, b) => a.order - b.order);

        return {
          ...parent,
          children,
          selectable: parent.items.length > 0,
        };
      })
      .filter((node) => node.selectable || node.children.length > 0)
      .sort((a, b) => a.order - b.order);
  });

  return { tree };
};
